import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Particles } from 'react-particles';
import { loadSlim } from "tsparticles-slim";
import { Instagram, Youtube, AlignJustify, Music } from 'lucide-react';
import type { Engine } from "tsparticles-engine";
import { api } from './lib/api';

// Components
import MusicPlayer from './components/playerComponents/MusicPlayer';
import BackgroundVisualizer from './components/BackgroundVisualizer';
import HLSLoadingBanner from './components/playerComponents/HLSLoadingBanner';

interface SocialLink {
  icon: string;
  url: string;
  label: string;
}

interface SiteContent {
  title: string;
  subtitle: string;
  social_links: SocialLink[];
}

// Create a memoized Particles component that won't re-render when parent state changes
const StableParticles = memo(function StableParticles() {
  const particlesInit = async (engine: Engine) => {
    await loadSlim(engine);
  };
  
  return (
    <Particles
      id="tsparticles"
      init={particlesInit}
      options={{
        background: {
          color: {
            value: "#000000",
          },
        },
        fpsLimit: 120,
        particles: {
          color: {
            value: "#ffffff",
          },
          links: {
            color: "#ffffff",
            distance: 150,
            enable: true,
            opacity: 0.5,
            width: 1,
          },
          move: {
            enable: true,
            outModes: {
              default: "bounce",
            },
            random: false,
            speed: 1,
            straight: false,
          },
          number: {
            density: {
              enable: true,
              area: 800,
            },
            value: 80,
          },
          opacity: {
            value: 0.5,
          },
          shape: {
            type: "square",
          },
          size: {
            value: { min: 1, max: 3 },
          },
        },
        detectRetina: true,
      }}
    />
  );
});

function App() {
  const [loading, setLoading] = useState(true);
  const [siteContent, setSiteContent] = useState<SiteContent>({
    title: 'N.S.T.Y. J',
    subtitle: 'Future of Sound',
    social_links: [
      { icon: 'Instagram', url: '#', label: 'Instagram' },
      { icon: 'Youtube', url: '#', label: 'YouTube' },
      { icon: 'AlignJustify', url: '#', label: 'Spotify' },
      { icon: 'Music', url: '#', label: 'SoundCloud' }
    ]
  });

  // Audio visualization state
  const [audioAnalyser, setAudioAnalyser] = useState<AnalyserNode | null>(null);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  
  // Handle visualizer updates from MusicPlayer
  const handleVisualizerUpdate = (analyser: AnalyserNode | null, isPlaying: boolean) => {
    setAudioAnalyser(analyser);
    setIsMusicPlaying(isPlaying);
  };

  // Debug when audio analyzer state changes
  useEffect(() => {
    // Effect body remains empty - removed console log
  }, [audioAnalyser, isMusicPlaying]);

  useEffect(() => {
    const fetchSiteContent = async () => {
      try {
        const response = await api.get('/site/content');
        if (response.data) {
          setSiteContent(response.data);
        }
      } catch (error) {
        console.error('Error fetching site content:', error);
      }
    };

    fetchSiteContent();
    setTimeout(() => setLoading(false), 2000);
  }, []);

  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'Instagram': return Instagram;
      case 'Youtube': return Youtube;
      case 'AlignJustify': return AlignJustify;
      case 'Music': return Music;
      default: return Instagram;
    }
  };

  return (
    <div className="h-screen bg-black text-white overflow-hidden">
      <HLSLoadingBanner />
      <div className="scanlines fixed inset-0 pointer-events-none z-50" />
      <div className="crt fixed inset-0 pointer-events-none z-40" />
      
      <AnimatePresence>
        {loading ? (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-[100] flex items-center justify-center"
          >
            <motion.div
              animate={{
                scale: [1, 2, 2, 1, 1],
                rotate: [0, 0, 270, 270, 0],
              }}
              transition={{
                duration: 2,
                ease: "easeInOut",
                times: [0, 0.2, 0.5, 0.8, 1],
                repeat: Infinity,
                repeatDelay: 1
              }}
              className="w-12 h-12 bg-white"
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Use the memoized StableParticles component instead */}
      <StableParticles />
      
      {/* Mobile song info - will be populated by MusicPlayer */}
      <div id="mobile-song-info" className="relative z-[70] w-full"></div>

      {/* Add portal target for mobile subtitles */}
      <div id="mobile-subtitles-portal" className="sm:hidden relative z-10 flex justify-center items-center w-full text-center px-4 top-[95px]" style={{ position: 'absolute'}}></div>
      
      {/* Background audio visualizer - position it after particles but before content */}
      <BackgroundVisualizer analyser={audioAnalyser} isPlaying={isMusicPlaying} />

      <main className="main-content relative z-10 h-screen flex flex-col items-center justify-center">
        <div className="absolute inset-0 dot-pattern opacity-5"></div>
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.8,
            delay: 0.5,
            ease: [0, 0.71, 0.2, 1.01]
          }}
          className="text-center"
        >
          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold mb-8 tracking-[0.2em] uppercase"
            animate={{
              opacity: [1, 0.8, 1],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            {siteContent.title}
          </motion.h1>
          <p className="text-xl md:text-2xl text-gray-400 uppercase tracking-[0.5em] mb-12">
            {siteContent.subtitle}
          </p>

          {/* Social Links */}
          <div className="flex justify-center space-x-8">
            {siteContent.social_links.map((link, index) => {
              const Icon = getIconComponent(link.icon);
              return (
                <motion.a
                  key={link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.2 }}
                  className="social-link w-12 h-12 bg-zinc-900 flex items-center justify-center hover:bg-zinc-800 transition-all duration-300"
                >
                  <span className="text-white">
                    <Icon size={24} />
                  </span>
                </motion.a>
              );
            })}
          </div>
        </motion.div>
      </main>

      {/* Fixed Music Player - Always render but hide during loading */}
      <div className={`fixed bottom-0 left-0 right-0 z-50 px-4 py-2 bg-black/80 backdrop-blur-lg border-t border-white/10 transition-opacity duration-500 ${loading ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <div className="max-w-4xl mx-auto">
          <MusicPlayer onVisualizerUpdate={handleVisualizerUpdate} />
        </div>
      </div>
    </div>
  );
}

export default App;